import { useCallback, useRef, useState } from 'react';
import { runtimeEnv } from '../config/runtimeEnv';

export type TurnstileAction = 'login' | 'register' | 'forgot_password' | 'reset_password' | 'resend_email';

export const useTurnstile = (action: TurnstileAction) => {
  const siteKey = runtimeEnv.turnstileSiteKey;
  const token = useRef<{ value: string; expiresAt: number } | null>(null);
  const submitting = useRef(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<'required' | 'failed' | null>(null);
  const [resetKey, setResetKey] = useState(0);

  const onVerify = useCallback((value: string) => {
    token.current = { value, expiresAt: Date.now() + 300000 };
    setIsVerified(true);
    setError(null);
  }, []);

  const onExpire = useCallback(() => {
    token.current = null;
    setIsVerified(false);
  }, []);

  const onError = useCallback(() => {
    token.current = null;
    setIsVerified(false);
    setError('failed');
  }, []);

  const retry = useCallback(() => {
    token.current = null;
    setIsVerified(false);
    setError(null);
    setResetKey((value) => value + 1);
  }, []);

  const run = useCallback(
    async (submit: (turnstileToken?: string) => Promise<unknown>): Promise<boolean> => {
      if (submitting.current) {
        return false;
      }

      const currentToken = token.current;

      if (siteKey && (!currentToken || currentToken.expiresAt <= Date.now())) {
        retry();
        setError('required');
        return false;
      }

      submitting.current = true;
      token.current = null;
      setIsVerified(false);

      try {
        await submit(siteKey ? currentToken?.value : undefined);
        return true;
      } catch {
        // The owning mutation displays the translated API error.
        return false;
      } finally {
        submitting.current = false;

        if (siteKey) {
          retry();
        }
      }
    },
    [retry, siteKey]
  );

  return {
    run,
    isReady: !siteKey || isVerified,
    widgetProps: { siteKey, action, resetKey, error, onVerify, onExpire, onError, retry },
  };
};
